/// <amd-module name="Repos/Views/ProjectPolicies/Components/BranchPatternSelector" />
import * as React from "react";
import { getInvalidBranchNameMessage } from "Repos/Common/Util/Ref";
import * as Resources from "Repos/Views/ProjectPolicies/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { format } from "VSS/Core/Util/String";
import { FormItem } from "VSSUI/FormItem";
import { Icon, IconSize } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { ITableColumn, TwoLineTableCell } from "VSSUI/Table";
import { TextField, TextFieldWidth } from "VSSUI/TextField";
import { IProjectPoliciesService, IRepoAndBranchName } from "../Contracts";
import { transformScopeForDisplay } from "../ReadableScope";

export interface IBranchPatternSelectorProps {
    projectPoliciesService: IProjectPoliciesService;
    branchPattern: ObservableValue<string>;
    isValidPattern: ObservableValue<boolean>;
}

type State = {
    showError: boolean;
    errorMessage: string | undefined;
};

export class BranchPatternSelector extends React.Component<IBranchPatternSelectorProps, State> {
    private timerManagement = new TimerManagement();
    private debouncedValidator: () => void;
    private debouncedGetHints: () => void;

    private readonly repoBranchColumn: ITableColumn<IRepoAndBranchName> = {
        id: "repoBranch",
        width: -100,

        renderCell: (rowIndex, columnIndex, tableColumn, tableItem) => (
            <TwoLineTableCell
                columnIndex={columnIndex}
                key={columnIndex}
                tableColumn={tableColumn}
                iconProps={{ iconName: "OpenSource" }}
                line1={tableItem.repoName}
                line2={tableItem.branchName}
            />
        )
    };

    constructor(props: IBranchPatternSelectorProps) {
        super(props);

        this.state = { showError: false, errorMessage: undefined };

        // Wait 300ms before displaying an error message while user is actively typing
        this.debouncedValidator = this.timerManagement.debounce(this.validate, 300);
        props.branchPattern.subscribe(this.debouncedValidator);

        // Wait 1000ms before querying for existing branches which match while user is actively typing
        this.debouncedGetHints = this.timerManagement.debounce(this.getHints, 1000);
        props.branchPattern.subscribe(this.debouncedGetHints);
    }

    public componentWillUnmount() {
        this.props.branchPattern.unsubscribe(this.debouncedValidator);
        this.props.branchPattern.unsubscribe(this.debouncedGetHints);
    }

    public render(): JSX.Element {
        const { projectPoliciesService, branchPattern } = this.props;
        const { existingBranchHints } = projectPoliciesService;

        return (
            <div className="project-policies-align-below-radio">
                <FormItem error={this.state.showError} message={this.state.errorMessage}>
                    <TextField
                        value={branchPattern}
                        onChange={(e, value) => {
                            branchPattern.value = value;
                        }}
                        placeholder={Resources.BranchPatternPlaceholder}
                        width={TextFieldWidth.auto}
                        required={true}
                        maxLength={400}
                    />
                    <div className="margin-top-8 secondary-text body-s">{Resources.BranchPatternExamples}</div>
                </FormItem>

                <Observer {...{ existingBranchHints }}>
                    {() => {
                        const hints = existingBranchHints.value;

                        if (hints == null || hints.branchPattern !== branchPattern.value) {
                            // No query reply, or reply on file is for some other query

                            return null;
                        } else if (hints.branchCount === 0) {
                            // Valid reply; no branches match

                            return (
                                <div className="margin-top-16 rhythm-vertical-16" key="nomatch" role="alert">
                                    <div>{Resources.MatchCount0}</div>
                                    <div>{Resources.WillMatchFutureBranches}</div>
                                </div>
                            );
                        } else {
                            // Valid reply and we have some repo/branch hints

                            let headingFormatString: string;

                            const hintCount = hints.existingBranches.length;

                            if (hints.branchCount === 1) {
                                headingFormatString = Resources.MatchCount1;
                            } else if (hints.branchCount <= hintCount) {
                                headingFormatString = Resources.MatchCountN;
                            } else {
                                headingFormatString = Resources.MatchCountTopN;
                            }

                            const headingText = format(
                                headingFormatString,
                                hints.branchCount.toLocaleString(),
                                hints.repoCount.toLocaleString(),
                                hintCount
                            );

                            return (
                                <div className="margin-top-16" key="match" role="alert">
                                    <div>{headingText}</div>
                                    <ul className="rhythm-vertical-16">
                                        {hints.existingBranches.map((repoBranch, i) => (
                                            <li key={i}>
                                                <div className="flex-column rhythm-vertical-4">
                                                    <div className="flex-row">
                                                        <Icon iconName="OpenSource" size={IconSize.small} className="icon-margin" />
                                                        <span className="text-ellipsis">{transformScopeForDisplay(repoBranch.branchName)}</span>
                                                    </div>
                                                    <div className="flex-row secondary-text body-s">
                                                        <span className="text-ellipsis">{repoBranch.repoName}</span>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        }
                    }}
                </Observer>
            </div>
        );
    }

    private readonly validate = (): void => {
        let pattern = this.props.branchPattern.value;

        if (!pattern) {
            // Blank is not valid, but we don't display an error
            this.props.isValidPattern.value = false;
            this.setState({
                showError: true,
                errorMessage: undefined
            });
        } else if (pattern === "*") {
            this.props.isValidPattern.value = true;
            this.setState({
                showError: false,
                errorMessage: undefined
            });
        } else {
            if (pattern.endsWith("/*")) {
                pattern = pattern.slice(0, -2);
            }

            let errMsg: string | undefined;

            if (pattern.indexOf("*") >= 0) {
                errMsg = Resources.Pattern_StarOnlyAtEnd;
            } else {
                errMsg = getInvalidBranchNameMessage(pattern);
            }

            this.setState({
                showError: !!errMsg,
                errorMessage: errMsg
            });

            this.props.isValidPattern.value = !errMsg;
        }
    };

    private readonly getHints = (): void => {
        if (!this.props.isValidPattern.value) {
            return;
        }

        const patt = this.props.branchPattern.value;

        this.props.projectPoliciesService.getExistingBranchHintsAsync(patt);
    };
}
